import React, { useState } from 'react';
import {
  buttonSurfaceStyle,
  badgeInnerBackgroundStyle,
  backgroundFillColor,
  fillContrastTextColor,
} from './blockBackgroundStyle';
import type { AccordionItem } from '@/store/editorSlice';
import type { ChartBar } from '@/store/editor/types/blocks';
import { interpolate } from '@/player/interpolate';
import type { VariableContext } from '@/player/variable-context';
import { RichTextContent } from './RichTextContent';
import {
  asRichField,
  hasRichText,
  richFieldToPlainText,
} from '@/types/rich-text';
import { safeHref } from '@/utils/isAllowedUrl';
import {
  useFormValues,
  type FormValuesContextValue,
} from './FormValuesContext';
import { MicroFeedbackReveal } from '@/player/microFeedbackReveal';
import { ChartBlockSvg } from './ChartBlockSvg';
// WEB-1994: место под картинку до её загрузки (докблок — в помощнике).
import { useImageSpaceReserve } from './imageSpaceReserve';

// WEB-2054: блоки портала отмены (WEB-1152/WEB-1395) и восемь блоков разбора
// референсов (WEB-926) переехали в свои куски рендерера —
// blocks/PortalBlocksRenderer.tsx и blocks/ReferenceBlocksRenderer.tsx.

// WEB-1692: настройки оформления по СМЫСЛОВЫМ РОЛЯМ текста (главный текст
// блока отдельно от вторичных подписей) — механика и решения там же.
import { blockTextRoles, type TextRoleStyle } from './semanticTextRoles';

import type { ButtonStyle } from '@/themes/theme.types';
import { useEditorPreview } from './EditorPreviewContext';

import { resolveContentTextColor } from './blockInnerSurface';
import { dividerLineStyle } from './dividerLineStyle';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
// WEB-3436: подсказка АВТОРУ о пустом рейтинге — маленький словарь подсказок
// превью (WEB-2057), не словарь конструктора целиком.
import { pickStrings } from '@/i18n';
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { AuthorHintBox } from './AuthorHintBox';
// WEB-916 п.2: цвет закрашенной звезды — токеном (прежний жёлтый давал 1.9:1).
import { RATING_STAR_COLOR } from './ratingStarColor';
// WEB-2054: общий вход кусков рендерера и общая обёртка блока — см. разрез
// рендерера по семьям видов (publicBlockChunkMap.ts).
import type { PublicExtendedBlockProps } from '@/player/publicExtendedBlockProps';
import { withExtendedBlockShell } from './extendedBlockShell';
// WEB-2054 (починка ревизии): чтение полей «ИИ-формы» блока стало общим с
// ядерным рендерером частых и денежных видов — одна копия на оба файла.
import { aiFields } from './aiShapedFields';
// Денежные виды (`plan_selector`, `payment_terms`, `price_breakdown_text`) и
// частые (`hero`, `bullet_list`, `icon_with_text`, `card`, `columns_2`,
// `testimonials`) переехали в ядро — почему, написано в шапке того файла.
// Здесь остаётся ОДНА дверь к ним: старые вызывающие (превью конструктора,
// существующие тесты) зовут этот рендерер по любому виду блока, и молча
// отдавать им пусто нельзя.
import { PublicBlockRendererCommon } from './PublicBlockRendererCommon';
import { CORE_COMMON_BLOCK_TYPES } from '@/player/publicBlockChunkMap';
// WEB-2920: доли хвоста — ветки видов, вынесенные из этого файла (рефакторинг
// большого файла, план state/batches/50/план/РЕФАКТОРИНГ-4-ФАЙЛОВ.md).
import { renderCountdown } from './public-blocks/CountdownRenderer';
import { renderLoaderProcessing } from './public-blocks/LoaderRenderer';
import { renderVideoEmbed } from './public-blocks/VideoEmbedRenderer';
import { renderFeatureChips } from './public-blocks/FeatureChipsRenderer';
import { renderTrustBadges } from './public-blocks/TrustBadgesRenderer';
import { renderBeforeAfter } from './public-blocks/BeforeAfterRenderer';
import { renderGallery } from './public-blocks/GalleryRenderer';
import { renderDownsellPopup } from './public-blocks/DownsellPopupRenderer';
import { renderCookieConsent } from './public-blocks/CookieConsentRenderer';
import { renderReturnToAppButton } from './public-blocks/ReturnToAppButtonRenderer';
import { renderRestorePurchases } from './public-blocks/RestorePurchasesRenderer';
import { renderCloseWebviewButton } from './public-blocks/CloseWebviewButtonRenderer';
import { renderPhoneInput } from './public-blocks/PhoneInputRenderer';
import { renderDatePicker } from './public-blocks/DatePickerRenderer';
import { renderNumberInput } from './public-blocks/NumberInputRenderer';
import { renderSelectDropdown } from './public-blocks/SelectDropdownRenderer';
import { renderNameField } from './public-blocks/NameFieldRenderer';
import { renderLikertScale } from './public-blocks/LikertScaleRenderer';
import { renderRatingStars } from './public-blocks/RatingStarsRenderer';
import { renderSliderRange } from './public-blocks/SliderRangeRenderer';
import { renderToggle } from './public-blocks/ToggleRenderer';
import { renderMultiStepQuestion } from './public-blocks/MultiStepQuestionRenderer';
import { renderImageChoice } from './public-blocks/ImageChoiceRenderer';
import {
  renderHeightPicker,
  renderWeightPicker,
} from './public-blocks/UnitPickerRenderer';
import { renderPhotoUpload } from './public-blocks/PhotoUploadRenderer';
// WEB-847: размеры сетки тарифов читает и панель конструктора, и её тесты —
// после переезда карточек тарифов в ядро имя остаётся доступным отсюда, чтобы
// переезд не менял чужие импорты.
export {
  PLAN_CARD_MIN_WIDTH,
  PLAN_GRID_GAP,
  planCardsPerRow,
} from './PublicBlockRendererCommon';

/**
 * WEB-1692 (ревизия пачки 6, находка 3.4) — КЕГЛЬ ЧИПА РЕЙТИНГА, ОДНО ЧИСЛО.
 *
 * Это число читают ДВА места: сама плашка чипа и подсчёт размера звёздочки
 * («звёздочка на пиксель крупнее оценки, как и сейчас» — решение карточки).
 * Пока оно стояло числом дважды, правка кегля плашки молча расходилась с
 * фолбэком звёздочки: 14 и 15 переставали быть парой, и «как сейчас»
 * переставало быть правдой ровно там, где автор ничего не задавал.
 */
const RATING_CHIP_FONT_SIZE = 14;

/** Насколько звёздочка чипа крупнее оценки — подгонка под высоту строки. */
const RATING_CHIP_STAR_FONT_SIZE_DELTA = 1;

/**
 * Palette-safe callout variant config.
 * Uses CSS variables so dark/custom themes render readable text.
 * Background and border come from the palette surface/border vars;
 * text color uses the semantic palette var for the variant type.
 */
const CALLOUT_VARIANT_COLOR: Record<string, string> = {
  info: 'var(--color-primary, #0958d9)',
  warning: 'var(--color-error, #d46b08)',
  success: 'var(--color-success, #16a34a)',
};

/**
 * WEB-2054: вход у всех неядерных кусков рендерера ОДИН и живёт отдельным
 * файлом (`player/publicExtendedBlockProps.ts`) — дверь отдаёт любому куску те
 * же пропсы, что раньше получал единственный общий рендерер. Второй набор
 * полей означал бы «до одного куска новое поле доехало, до другого нет».
 */
type PublicBlockRendererExtendedProps = PublicExtendedBlockProps;

/**
 * WEB-686 — controlled accordion (replaces native <details>/<summary>, which
 * snaps open/closed with no animation). Uses the CSS grid-template-rows
 * 0fr/1fr trick so the answer's height animates smoothly without needing a
 * measured pixel height. The last item has no bottom border so the list
 * does not end with a stray line.
 */
/**
 * WEB-1692 юнит 3 (репорт владельца 24.08 «не хватает настройки выбора размера
 * шрифта для заголовка и для выпадающего контента»). Случай крайний: сюда
 * приходил ТОЛЬКО список пунктов, а кегли (16 у заголовка, 15 у ответа) лежали
 * числами внутри — работа поэтому на два шага, сперва провести настройки до
 * компонента. Накладки спредятся ПОСЛЕ прежних строк и при пустых полях равны
 * пустому объекту: старая воронка не меняется ни на байт.
 */
function AccordionBlock({
  items,
  titleStyle,
  bodyStyle,
  richText,
}: {
  items: AccordionItem[];
  titleStyle?: TextRoleStyle;
  bodyStyle?: TextRoleStyle;
  /** WEB-2923: дверь подстановки фасада (`ctx.richText`) — тексты автора. */
  richText: ExtendedRenderContext['richText'];
}): React.ReactElement {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return (
    <div
      style={{ marginBottom: 16, color: 'var(--color-text-primary, inherit)' }}
    >
      {items.map((item, i) => {
        const isOpen = openIndex === i;
        const isLast = i === items.length - 1;
        return (
          <div
            key={i}
            data-testid={`accordion-item-${i}`}
            // data-has-border-bottom mirrors the inline borderBottom below in a
            // form that survives jsdom's CSS parsing (which drops border
            // shorthands using a var() color entirely) — asserted in tests
            // instead of parsing the style attribute.
            data-has-border-bottom={!isLast}
            style={{
              borderBottom: isLast
                ? 'none'
                : '1px solid var(--color-border-primary, #f0f0f0)',
            }}
          >
            <button
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : i)}
              aria-expanded={isOpen}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                background: 'none',
                border: 'none',
                padding: '12px 0',
                margin: 0,
                cursor: 'pointer',
                fontWeight: 500,
                // ⚠ WEB-1692, НАХОДКА СВЕРХ КАРТОЧКИ: зашитые здесь 16 и 500
                // СЕГОДНЯ НЕ ДЕЙСТВУЮТ — строкой ниже стоит СОКРАЩЁННОЕ
                // свойство `font: 'inherit'`, а оно сбрасывает всё семейство,
                // включая объявленные выше кегль и насыщенность. Заголовок
                // пункта наследует шрифт блока, и это его сегодняшний вид;
                // трогать его нельзя (живые воронки, распоряжение 24.08).
                //
                // Из-за этого накладка автора НЕ МОЖЕТ идти простым спредом: в
                // объектном литерале повторный ключ сохраняет ПЕРВУЮ позицию,
                // поэтому авторский кегль применился бы ДО `font` и был бы им
                // же стёрт. Поэтому зашитое число объявляется, только пока
                // автор своего не задал, а его значение приходит ПОСЛЕ `font`.
                ...(titleStyle?.fontSize === undefined ? { fontSize: 16 } : {}),
                color: 'inherit',
                font: 'inherit',
                // Накладка автора — ПОСЛЕ прежних строк; пусто = {}.
                ...(titleStyle ?? {}),
              }}
            >
              {richText(item.title)}
            </button>
            <div
              data-testid={`accordion-body-${i}`}
              style={{
                display: 'grid',
                gridTemplateRows: isOpen ? '1fr' : '0fr',
                transition: 'grid-template-rows 0.25s ease',
                overflow: 'hidden',
              }}
            >
              <div style={{ minHeight: 0, overflow: 'hidden' }}>
                <p
                  style={{
                    padding: '0 0 12px',
                    fontSize: 15,
                    color: 'var(--color-text-secondary, #595959)',
                    lineHeight: 1.6,
                    margin: 0,
                    // WEB-1692: накладка автора — ПОСЛЕ прежних строк; пусто = {}.
                    ...(bodyStyle ?? {}),
                  }}
                >
                  {richText(item.body)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * WEB-2920 — КОНТЕКСТ ДОЛЕЙ ХВОСТА. Ветки видов переезжают из этого файла в
 * `public-blocks/<Вид>Renderer.tsx` функциями отрисовки; замыкания фасада
 * (цвет текста блока, язык и подстановки, стиль-пак) доходят до них этим
 * объектом. Хуки остаются в фасаде: функция отрисовки зовётся из `switch`, и
 * хук внутри неё нарушил бы порядок хуков (рисуется одна ветка за рендер).
 * Поле добавляется, когда его начинает читать перенесённая ветка.
 */
export interface ExtendedRenderContext {
  block: PublicExtendedBlockProps['block'];
  varCtx?: VariableContext;
  stylePackId?: string;
  /** Цвет текста ЭТОГО блока (лестница WEB-1702) или токен темы. */
  primaryColor: string;
  /** Цвет текста ЭТОГО блока по лестнице WEB-1702 без запасного токена темы. */
  contentTextColor?: string;
  /** Общий цвет текста воронки — проп рендерера, без лестницы блока. */
  textColor?: string;
  /** Стиль кнопок воронки — проп рендерера. */
  buttonStyle?: ButtonStyle;
  /** Цены Stripe воронки — проп рендерера. */
  stripePrices?: PublicExtendedBlockProps['stripePrices'];
  /** F6: подстановка переменных в простое текстовое поле. */
  interp: (text: string | undefined) => string;
  /** WEB-672 2a: насыщенный текст с подстановками. */
  richText: (
    value: string | undefined,
    fallback?: React.ReactNode,
  ) => React.ReactElement;
  /** WEB-672 2a: «есть ли текст» у насыщенного поля (простая строка — как раньше). */
  hasText: (value: string | undefined) => boolean;
  /**
   * WEB-222: ответы посетителя — `useFormValues()` фасада (хук стоит до
   * `switch`). Ветки вопросов читают ответ из `values` и пишут через `setValue`.
   */
  values: FormValuesContextValue['values'];
  setValue: FormValuesContextValue['setValue'];
  /** WEB-809 wave 2b: подпись варианта — displayLabel (rich) или label. */
  optionDisplay: (opt: {
    label: string;
    displayLabel?: string;
  }) => React.ReactNode;
  /** WEB-1994: резерв места под снимок «до» — хук фасада, стоит до `switch`. */
  beforeAfterBeforeImage: ReturnType<typeof useImageSpaceReserve>;
  /** WEB-1994: резерв места под снимок «после» — хук фасада, стоит до `switch`. */
  beforeAfterAfterImage: ReturnType<typeof useImageSpaceReserve>;
}

export function PublicBlockRendererExtended({
  block,
  textColor,
  varCtx,
  stripePrices,
  buttonStyle,
  stepPosition,
  stylePackId,
  screenBlocks,
  funnelBlocks,
  hiddenBlockIds,
  followsExplicitSpacing,
}: PublicBlockRendererExtendedProps): React.ReactElement | null {
  // WEB-846: гард превью редактора для интерактивных ссылок (urgency_banner) —
  // клик не исполняет боевой переход, только всплывает до выделения блока.
  const { isEditorPreview } = useEditorPreview();
  // WEB-1702 п.2: цвет текста ЭТОГО блока сильнее общего цвета воронки —
  // лестница слоёв в blockInnerSurface.tsx (resolveContentTextColor). Точка
  // одна на рендерер: всё, что ниже читает contentTextColor вместо textColor.
  const contentTextColor = resolveContentTextColor(block, textColor);
  const primaryColor = contentTextColor ?? 'var(--color-text-primary, inherit)';
  /** F6: apply interpolation to plain text fields when varCtx is present. */
  const interp = (text: string | undefined): string =>
    text == null ? '' : varCtx ? interpolate(text, varCtx) : text;
  /** WEB-2923: то же для поля, у которого «не задано» ведёт к умолчанию. */
  const interpOptional = (text: string | undefined): string | undefined =>
    text == null ? undefined : interp(text);

  /**
   * WEB-672 2a: rich-field render path (mirror of RICH_FIELDS_BY_BLOCK_TYPE).
   * richText() interpolates like the legacy interp() did.
   * WEB-2923: у хвоста больше нет пути «насыщенный текст БЕЗ подстановки»
   * (бывший richTextStatic у нумерованного списка, аккордеона и подписи
   * кнопки оффера удержания): покупатель видел сырое `{{answer.…}}`. Каждый
   * текст автора идёт через interp/richText — сторож
   * `__tests__/authorTextInterpolation.web2923.guard.test.tsx`.
   */
  const richText = (value: string | undefined, fallback?: React.ReactNode) => (
    <RichTextContent
      value={asRichField(value)}
      varCtx={varCtx}
      fallback={fallback}
    />
  );
  /** Truthiness gate replacement for rich fields (parity for plain strings). */
  const hasText = (value: string | undefined): boolean =>
    hasRichText(asRichField(value));

  /**
   * WEB-809 wave 2b: подпись варианта — displayLabel (rich) при наличии,
   * иначе plain label. label остаётся значением ответа (его не трогаем).
   * WEB-2923: обе формы подписи — через дверь подстановки (richText/interp).
   */
  const optionDisplay = (opt: {
    label: string;
    displayLabel?: string;
  }): React.ReactNode =>
    hasRichText(asRichField(opt.displayLabel))
      ? richText(opt.displayLabel)
      : interp(opt.label);
  // WEB-222: answer capture — wire onChange for all extended input block types.
  const { values, setValue } = useFormValues();
  // Починка ревизии («сломанная картинка оставляет пустую коробку 4:3»):
  // резерв места (WEB-1994) держится НАВСЕГДА, если картинка никогда не
  // загрузится и `onError` его не снимает — снимки «до»/«после» применяли
  // резерв без него, в отличие от основного блока image. Хуки обязаны стоять
  // ЗДЕСЬ, до switch/IIFE ниже (правила хуков: switch по block.type исполняет
  // только ОДНУ ветку за рендер) — для block.type, у которого своего
  // поля-картинки нет, imageUrl всегда undefined и хук — no-op (сам
  // resolveImageSpace/reserve не зовётся).
  //
  // WEB-2054 (свежий взгляд, проход 1): обложка карточки уехала вместе с
  // веткой `card` в ядерный общий файл, и её резерв объявлялся здесь впустую.
  const beforeAfterBeforeImage = useImageSpaceReserve(
    {
      width: '100%',
      borderRadius: 'var(--radius-image, var(--border-radius-md, 8px))',
      display: 'block',
    },
    block.beforeImageUrl,
  );
  const beforeAfterAfterImage = useImageSpaceReserve(
    {
      width: '100%',
      borderRadius: 'var(--radius-image, var(--border-radius-md, 8px))',
      display: 'block',
    },
    block.afterImageUrl,
  );
  // WEB-2054 (починка ревизии): виды, уехавшие в ЯДРО, рисует ядерный
  // `PublicBlockRendererCommon`. Дверь стоит ЗДЕСЬ, после хуков и ДО общей
  // обёртки блока: обёртку ставит сам ядерный рендерер, и вторая поверх неё
  // добавила бы блоку лишние отступы и поверхность. Список — из той же карты
  // кусков, что читает публичный вход: второго перечня видов не заводим.
  if (CORE_COMMON_BLOCK_TYPES.includes(block.type)) {
    return (
      <PublicBlockRendererCommon
        block={block}
        textColor={textColor}
        varCtx={varCtx}
        stripePrices={stripePrices}
        buttonStyle={buttonStyle}
        stepPosition={stepPosition}
        stylePackId={stylePackId}
        screenBlocks={screenBlocks}
        funnelBlocks={funnelBlocks}
        hiddenBlockIds={hiddenBlockIds}
        followsExplicitSpacing={followsExplicitSpacing}
      />
    );
  }

  // WEB-2920: замыкания, которые читают перенесённые в public-blocks/ ветки, —
  // объектом-контекстом, посчитанные здесь один раз, как и до переноса.
  const ctx: ExtendedRenderContext = {
    block,
    varCtx,
    stylePackId,
    primaryColor,
    contentTextColor,
    textColor,
    buttonStyle,
    stripePrices,
    interp,
    richText,
    hasText,
    beforeAfterBeforeImage,
    beforeAfterAfterImage,
    values,
    setValue,
    optionDisplay,
  };

  // WEB-672 atom E: switch inside an IIFE so withBlockFont applies uniformly
  // to every extended case (per-block font override, no layout impact).
  const rendered = ((): React.ReactElement | null => {
    switch (block.type) {
      // Layout & Visual
      case 'divider':
        // WEB-684: настраиваемые длина (dividerWidthPx) и толщина
        // (dividerHeightPx) линии; без них — прежний вид (full-width, 1px).
        // WEB-1737: сборка стиля вынесена в чистую функцию dividerLineStyle —
        // там же объяснён конфликт «пунктир × скругление» и почему пунктир со
        // скруглением рисуется фоном, а не границей. Вынос не косметика:
        // градиент в тестовой среде не виден вовсе (jsdom его выбрасывает),
        // и проверяемым стиль остаётся только как значение функции.
        return <hr style={dividerLineStyle(block)} />;

      case 'spacer':
        // Гибкий отступ рисует только МИНИМАЛЬНУЮ высоту (по умолчанию 0):
        // растягивает его обёртка визарда (flexSpacer.tsx), не этот div —
        // между ним и колонкой экрана бывает до четырёх обёрток.
        return (
          <div
            style={{
              height: block.spacerFlexible
                ? (block.heightPx ?? 0)
                : (block.heightPx ?? 32),
            }}
            aria-hidden="true"
          />
        );

      case 'numbered_list':
        return (
          <ol
            style={{
              marginBottom: 16,
              paddingLeft: 24,
              color: 'var(--color-text-primary, inherit)',
            }}
          >
            {(block.listItems ?? []).map((item, i) => (
              <li
                key={i}
                style={{ fontSize: 16, lineHeight: 1.6, marginBottom: 4 }}
              >
                {richText(item)}
              </li>
            ))}
          </ol>
        );

      case 'accordion': {
        // WEB-1692 юнит 3: две роли текста — заголовок пункта и раскрывающийся
        // текст под ним (ровно так владелец и сформулировал).
        const accordionRoles = blockTextRoles(block, 'accordion');
        return (
          <AccordionBlock
            items={block.accordionItems ?? []}
            titleStyle={accordionRoles.title}
            bodyStyle={accordionRoles.secondary}
            richText={richText}
          />
        );
      }

      // Forms
      case 'phone_input':
        return renderPhoneInput(ctx);

      case 'date_picker':
        return renderDatePicker(ctx);

      case 'number_input':
        return renderNumberInput(ctx);

      case 'select_dropdown':
        return renderSelectDropdown(ctx);

      case 'micro_feedback': {
        // Атом 8 (референс 12min): мягкая реакция между вопросами. Цвет берётся
        // из палитры, а не задаётся жёстко, иначе блок выпадал бы из темы.
        // WEB-926: полноэкранный режим (12min, Asana Rebel) — маскот со
        // свечением, заголовок, абзац. Поле не задано — прежняя плашка.
        const fullscreen = block.feedbackLayout === 'fullscreen';
        const mascot =
          fullscreen && hasText(block.feedbackEmoji)
            ? block.feedbackEmoji
            : undefined;
        if (
          !hasText(block.content) &&
          !hasText(block.feedbackDetail) &&
          !mascot
        )
          return null;
        const tone = block.feedbackTone ?? 'support';
        const accent =
          tone === 'success'
            ? 'var(--color-success, #52c41a)'
            : tone === 'fact'
              ? 'var(--color-text-secondary, #8c8c8c)'
              : 'var(--color-primary, #4094f7)';
        if (fullscreen) {
          // WEB-986: маскот бывает и НЕ эмодзи (буква/слово) — гиганский кегль
          // эмодзи для текста нечитаем, буква получает свой размер.
          const mascotIsEmoji =
            !!mascot && /\p{Extended_Pictographic}/u.test(mascot);
          return (
            <div
              data-testid="micro-feedback"
              data-tone={tone}
              data-layout="fullscreen"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 16,
                // Экран целиком, но без вьюпортной высоты: блок живёт внутри колонки визарда.
                // WEB-986: потолок в px — в канвасе превью vh меряет вьюпорт
                // БРАУЗЕРА и раздувал секцию сильнее самого телефона.
                minHeight: 'min(calc(var(--vp-h, 100vh) * 0.55), 480px)',
                padding: '24px 8px',
                marginBottom: 16,
              }}
            >
              {mascot && (
                <span
                  data-testid="micro-feedback-emoji"
                  data-mascot-kind={mascotIsEmoji ? 'emoji' : 'text'}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // WEB-986: кружок масштабируется от ширины колонки с
                    // потолком — фикс 132px занимал пол-превью (колонка ≈268px)
                    // и вместе со свечением наезжал на заголовок.
                    width: '44%',
                    maxWidth: 132,
                    minWidth: 88,
                    aspectRatio: '1',
                    fontSize: mascotIsEmoji ? 64 : 40,
                    fontWeight: mascotIsEmoji ? undefined : 700,
                    lineHeight: 1,
                    borderRadius: '50%',
                    // Свечение — тот же акцент тона, размытый в подложку: цвет
                    // берётся из пака, а не задаётся жёстко. WEB-986: радиус
                    // ужат (48→28) и у маскота свой нижний отступ — свечение
                    // не заползает под заголовок.
                    background: `radial-gradient(circle, color-mix(in srgb, ${accent} 22%, transparent) 0%, transparent 70%)`,
                    boxShadow: `0 0 28px color-mix(in srgb, ${accent} 28%, transparent)`,
                    marginBottom: 8,
                  }}
                >
                  {mascot}
                </span>
              )}
              {hasText(block.content) && (
                <div
                  style={{
                    fontSize: block.feedbackTitleFontSize ?? 24,
                    fontWeight: 700,
                    lineHeight: 1.25,
                    color: 'var(--color-text-primary, inherit)',
                    // WEB-1430: пользовательский цвет — поверх дефолта темы;
                    // у «Целого экрана» нет плашки-подложки, поэтому только
                    // текстовые настройки (фон неприменим).
                    ...(block.feedbackTextColor
                      ? { color: block.feedbackTextColor }
                      : {}),
                  }}
                >
                  {richText(block.content)}
                </div>
              )}
              {hasText(block.feedbackDetail) && (
                <p
                  style={{
                    fontSize: block.feedbackDetailFontSize ?? 16,
                    lineHeight: 1.5,
                    maxWidth: 420,
                    color: 'var(--color-text-secondary, #595959)',
                    ...(block.feedbackTextColor
                      ? { color: block.feedbackTextColor }
                      : {}),
                  }}
                >
                  {richText(block.feedbackDetail)}
                </p>
              )}
            </div>
          );
        }
        return (
          <MicroFeedbackReveal
            block={block}
            screenBlocks={screenBlocks}
            values={values}
            editor={isEditorPreview}
          >
            <div
              data-testid="micro-feedback"
              data-tone={tone}
              data-layout="inline"
              style={{
                marginBottom: 16,
                // Подложка — тот же акцент с малой непрозрачностью: работает и на
                // светлых, и на тёмных темах без отдельного набора цветов.
                background: 'var(--color-bg-surface, #fafafa)',
                // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
                color:
                  'var(--color-text-on-surface, var(--color-text-primary, inherit))',
                // Ревизия пачки 16, С-1: badgeInnerBackgroundStyle (не
                // blockBackgroundStyle) — та же механика Нет/Цвет/Полупрозрачный/
                // Блюр/Стекло (blockBackgroundStyle.tsx), но ДОКЛЕИВАЕТ color
                // через pickOnPrimaryColor(color) поверх заливки. Без этого
                // своя solid-подложка красилась, а текст оставался on-surface
                // ТЕМЫ — класс WEB-966 повторно (тёмное-на-тёмном на #111).
                // Ключ встаёт ПОСЛЕ дефолтного on-surface литерала выше (спред
                // ниже по тексту побеждает) и ДО пользовательского
                // feedbackTextColor — тот живёт в style заголовка/пояснения
                // ниже и всегда побеждает как прямой color на самом элементе,
                // независимо от цвета, унаследованного с этой обёртки.
                // Absent/style:'none' -> {} -> ничего не меняется (byte-for-byte
                // как раньше, badgeInnerBackgroundStyle зеркалит этот контракт).
                ...badgeInnerBackgroundStyle(block.feedbackInnerBackground),
                // М-1: borderLeft — ПОСЛЕ спреда. style:'glass' несёт свой
                // border-шорткат (backgroundFillStyle) — border-шорткат в
                // инлайн-style-строке стирает border-left, если тот объявлен
                // раньше (тот же класс проблем, что и порядок border/padding).
                // Здесь borderLeft объявляется позже итоговой style-строки и
                // переигрывает border обратно — акцентная полоса тона не исчезает.
                borderLeft: `3px solid ${accent}`,
                // М-2: полный литерал радиуса — продублирован ПОСЛЕ спреда, а
                // не только в базовых полях выше, иначе фон плашки укоротил бы
                // фолбэк (badgeInnerBackgroundStyle сегодня borderRadius не
                // трогает, но контракт не завязан на это молча).
                borderRadius:
                  'var(--radius-card, var(--border-radius-lg, 12px))',
                // Свой паддинг плашки — ПОСЛЕ спреда той же причине.
                padding: '12px 16px',
              }}
            >
              {hasText(block.content) && (
                <div
                  style={{
                    fontWeight: 600,
                    marginBottom: hasText(block.feedbackDetail) ? 4 : 0,
                    fontSize: block.feedbackTitleFontSize,
                    // WEB-1430: пользовательский цвет — поверх дефолта темы
                    // (on-surface); absent = поведение не меняется.
                    ...(block.feedbackTextColor
                      ? { color: block.feedbackTextColor }
                      : {}),
                  }}
                >
                  {richText(block.content)}
                </div>
              )}
              {hasText(block.feedbackDetail) && (
                <div
                  style={{
                    fontSize: block.feedbackDetailFontSize ?? 14,
                    // Текст стоит НА плашке (--color-bg-surface) — цвет обязан
                    // резолвиться через on-surface (WEB-966): фото-фон с вуалью
                    // пиннит --color-text-secondary белым, и пояснение исчезало
                    // на светлой плашке (живой случай PM 17.08).
                    color:
                      'var(--color-text-on-surface, var(--color-text-secondary, #595959))',
                    // WEB-1430: пользовательский цвет — поверх дефолта темы.
                    ...(block.feedbackTextColor
                      ? { color: block.feedbackTextColor }
                      : {}),
                  }}
                >
                  {richText(block.feedbackDetail)}
                </div>
              )}
            </div>
          </MicroFeedbackReveal>
        );
      }

      case 'rating_chip': {
        // Атом 8 (референс Dressly): компактная пилюля соцпруфа над заголовком.
        // Без оценки чип не имеет смысла — не рисуем пустую плашку.
        if (typeof block.ratingValue !== 'number') return null;
        const scale = block.ratingScale ?? 5;
        // WEB-1692: владелец просил размер текста, а текстов в чипе два —
        // сама оценка и подпись об аудитории; поэтому и ролей две, тем же
        // механизмом. Цвет владелец не просил, но механизм даёт его даром —
        // карточка это прямо разрешает.
        //
        // ЗВЁЗДОЧКА ИДЁТ ЗА ОЦЕНКОЙ. Её 15px против 14px чипа — подгонка под
        // высоту строки, а не случайность (так и сказано в карточке: «по уму
        // да, с тем же соотношением»). Поэтому размер звёздочки считается от
        // кегля оценки плюс тот же пиксель; пусто = прежние 14 и 15.
        const chipRoles = blockTextRoles(block, 'rating_chip');
        const chipStarFontSize =
          (chipRoles.title.fontSize ?? RATING_CHIP_FONT_SIZE) +
          RATING_CHIP_STAR_FONT_SIZE_DELTA;
        const justify =
          block.chipAlign === 'left'
            ? 'flex-start'
            : block.chipAlign === 'right'
              ? 'flex-end'
              : 'center';
        return (
          <div
            data-testid="rating-chip-wrap"
            style={{
              display: 'flex',
              justifyContent: justify,
              marginBottom: 16,
            }}
          >
            <span
              data-testid="rating-chip"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 14px',
                borderRadius: 'var(--border-radius-button, 999px)',
                background: 'var(--color-bg-surface, #f5f5f5)',
                fontSize: RATING_CHIP_FONT_SIZE,
                color:
                  'var(--color-text-on-surface, var(--color-text-primary, inherit))',
              }}
            >
              <span
                aria-hidden
                data-testid={`rating-chip-star-${block.id}`}
                style={{ fontSize: chipStarFontSize }}
              >
                ⭐
              </span>
              {/* Число крупнее подписи — глаз цепляется за оценку. */}
              <span
                data-testid={`rating-chip-value-${block.id}`}
                style={{ fontWeight: 700, ...chipRoles.title }}
              >
                {/* WEB-967: «из» было русским на всех языках — теперь словарь. */}
                {getPublicQuizStrings(varCtx?.system?.locale).ratingOutOf(
                  block.ratingValue,
                  scale,
                )}
              </span>
              {hasText(block.ratingAudience) && (
                <>
                  <span
                    aria-hidden
                    data-testid={`rating-chip-separator-${block.id}`}
                    style={{
                      color: 'var(--color-border-primary, #d9d9d9)',
                      // Черта — разделитель, а не текст: за кеглем подписи она
                      // идёт (иначе разъедется строка), но авторский ЦВЕТ на
                      // неё не переносится — он про текст.
                      ...(chipRoles.secondary.fontSize !== undefined
                        ? { fontSize: chipRoles.secondary.fontSize }
                        : {}),
                    }}
                  >
                    |
                  </span>
                  <span
                    data-testid={`rating-chip-audience-${block.id}`}
                    style={{ fontWeight: 600, ...chipRoles.secondary }}
                  >
                    {richText(block.ratingAudience)}
                  </span>
                </>
              )}
            </span>
          </div>
        );
      }

      case 'likert_scale':
        return renderLikertScale(ctx);

      case 'rating_stars':
        return renderRatingStars(ctx);

      case 'slider_range':
        return renderSliderRange(ctx);

      case 'toggle':
        return renderToggle(ctx);

      case 'name_field':
        return renderNameField(ctx);

      // Media & Social proof
      case 'video_embed':
        return renderVideoEmbed(ctx);

      case 'feature_chips':
        return renderFeatureChips(ctx);

      case 'review_stars': {
        // WEB-1692: цифра рейтинга отдельно от подписи под звёздами (репорт
        // владельца 24.08). Ряд звёзд ненастраиваем — решение записано в
        // semanticTextRoles.ts: владелец про них не просил, а их цвет — общая
        // константа, одна на «Отзывы» и «Рейтинг отзывов».
        // WEB-3436 (решение владельца 16.10): пустой рейтинг СКРЫВАЕТ блок у
        // покупателя — прежнее `?? 4.5` показывало выдуманную оценку там, где
        // её не задал ни автор, ни генерация (WEB-2962 оставляет поле пустым).
        // В превью конструктора блок не пропадает молча (шрам WEB-846 «блок-
        // невидимка»): автору — плашка-подсказка, без цифры и звёзд. Ноль —
        // это значение, а не пустота.
        const rating = block.reviewRating;
        if (typeof rating !== 'number' || !Number.isFinite(rating)) {
          return isEditorPreview ? (
            <AuthorHintBox
              testId={`review-stars-rating-empty-${block.id}`}
              compact
            >
              {pickStrings(authorHintsStrings).reviewStarsRatingEmpty}
            </AuthorHintBox>
          ) : null;
        }
        const roles = blockTextRoles(block, 'review_stars');
        return (
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div
              data-testid={`review-stars-value-${block.id}`}
              style={{
                fontSize: 48,
                fontWeight: 700,
                color: 'var(--color-text-primary, #262626)',
                ...roles.title,
              }}
            >
              {rating}
            </div>
            <div
              data-testid={`review-stars-row-${block.id}`}
              style={{
                fontSize: 24,
                color: RATING_STAR_COLOR,
                marginBottom: 4,
              }}
            >
              {'★'.repeat(Math.round(rating))}
            </div>
            {block.reviewCount !== undefined && (
              // WEB-785: "Based on N reviews" was a hardcoded English wrapper
              // with no schema field for the phrase itself — localize by locale.
              // WEB-1429: authorable override with a {count} placeholder,
              // resolved to reviewCount — absent/blank falls back to the
              // locale dictionary sentence above.
              <div
                data-testid={`review-stars-caption-${block.id}`}
                style={{
                  fontSize: 14,
                  color: 'var(--color-text-secondary, #8c8c8c)',
                  ...roles.secondary,
                }}
              >
                {(() => {
                  const custom = interp(block.reviewsBasedOnLabel).trim();
                  return custom
                    ? custom.replace(/\{count\}/g, String(block.reviewCount))
                    : getPublicQuizStrings(
                        varCtx?.system?.locale,
                      ).reviewsBasedOn(block.reviewCount);
                })()}
              </div>
            )}
          </div>
        );
      }

      case 'trust_badges':
        return renderTrustBadges(ctx);

      case 'guarantee_badge': {
        // Запрос владельца продукта 10.08: цвет рамки настраивается в панели
        // свойств (badgeBorderColor) вместо зашитого денежного акцента.
        // Absent = как раньше (var(--accent-money, var(--color-success))) —
        // WEB-925: гарантия возврата — денежный акцент пака; без пака
        // переменной нет → фолбэк = сегодняшний success.
        const badgeBorder = block.badgeBorderColor
          ? `2px solid ${block.badgeBorderColor}`
          : '2px solid var(--accent-money, var(--color-success, #16a34a))';
        // Запрос владельца продукта 10.08: фон ВНУТРЕННЕЙ области значка —
        // та же механика Нет/Цвет/Полупрозрачный/Блюр/Стекло, что у «Фон
        // блока»/«Фон кнопки» (blockBackgroundStyle.tsx). Absent/style:'none'
        // -> {} -> зашитые background/color ниже НЕ перекрываются, рендер
        // byte-for-byte как раньше.
        const badgeInnerFill = badgeInnerBackgroundStyle(
          block.badgeInnerBackground,
        );
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '16px 20px',
              background: 'var(--color-bg-surface, #f8fafc)',
              // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
              color:
                'var(--color-text-on-surface, var(--color-text-primary, inherit))',
              border: badgeBorder,
              borderRadius: 'var(--border-radius-lg, 12px)',
              marginBottom: 16,
              // Мержится ПОСЛЕДНИМ — порядок ключей решает, чей
              // backgroundColor/color/border побеждает (тот же приём, что у
              // withBlockBackground/buttonBackgroundStyle): при заданном
              // badgeInnerBackground перекрывает background/color выше, у
              // glass — ещё и border (несёт свою тонкую рамку сама).
              ...badgeInnerFill,
            }}
          >
            <span style={{ fontSize: 32 }}>{block.badgeIcon || '✓'}</span>
            <span
              style={{
                // Дозаказ владельца продукта: настоящий px-размер перекрывает
                // прежний фиксированный 16px.
                fontSize: block.fontSize ?? 16,
                fontWeight: 600,
                // WEB-966: текст на светлой подложке гарантии — не цвет
                // страницы. При заданном badgeInnerBackground — контраст-
                // безопасный цвет ПОВЕРХ ПОЛЬЗОВАТЕЛЬСКОГО фона
                // (badgeInnerFill.color), а не жёстко on-surface (тот
                // предполагает светлую var(--color-bg-surface)).
                color:
                  badgeInnerFill.color ??
                  'var(--color-text-on-surface, var(--color-text-primary, #0f172a))',
              }}
            >
              {richText(block.content)}
            </span>
          </div>
        );
      }

      case 'before_after':
        return renderBeforeAfter(ctx);

      case 'gallery':
        return renderGallery(ctx);

      // Urgency & Conversion
      case 'countdown':
        return renderCountdown(ctx);

      case 'stock_badge': {
        // WEB-697: варианты вида значка — pill (дефолт, как раньше), ribbon
        // (лента с уголками), rectangle, plain (прозрачный, без бордера),
        // filled (закрашенный фон).
        const variant = block.stockBadgeVariant ?? 'pill';
        const baseBadge: React.CSSProperties = {
          display: 'inline-block',
          padding: '8px 20px',
          // Дозаказ владельца продукта: настоящий px-размер перекрывает
          // прежний фиксированный 15px.
          fontSize: block.fontSize ?? 15,
          fontWeight: 700,
        };
        const variantStyle: React.CSSProperties =
          variant === 'ribbon'
            ? {
                background: 'var(--color-error, #d4380d)',
                color: '#fff',
                padding: '8px 28px',
                clipPath:
                  'polygon(0 0, 100% 0, calc(100% - 12px) 50%, 100% 100%, 0 100%, 12px 50%)',
              }
            : variant === 'rectangle'
              ? {
                  background: 'var(--color-bg-surface, #fff7e6)',
                  border: '2px solid var(--color-error, #ffd591)',
                  borderRadius: 'var(--border-radius-sm, 4px)',
                  color: 'var(--color-error, #d46b08)',
                }
              : variant === 'plain'
                ? {
                    background: 'transparent',
                    color: 'var(--color-error, #d46b08)',
                  }
                : variant === 'filled'
                  ? {
                      background: 'var(--color-error, #d4380d)',
                      color: '#fff',
                      borderRadius: 'var(--border-radius-md, 8px)',
                    }
                  : {
                      background: 'var(--color-bg-surface, #fff7e6)',
                      border: '2px solid var(--color-error, #ffd591)',
                      // shape-fixed (WEB-696): stock-бейдж — пилюля в любом
                      // стиль-паке, радиус тут форма, а не тема.
                      borderRadius: 24,
                      color: 'var(--color-error, #d46b08)',
                    };
        return (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 16,
            }}
          >
            <span style={{ ...baseBadge, ...variantStyle }}>
              {richText(block.content)}
            </span>
          </div>
        );
      }

      case 'urgency_banner': {
        // WEB-698: позиция кнопки — default (слева от текста, как раньше),
        // top (кнопка над текстом) или bottom (под текстом); в column-режимах
        // всё центрируется по вертикали и горизонтали.
        const pos = block.urgencyButtonPosition ?? 'default';
        // WEB-1711 — ЗАЛИВКА БАННЕРА И ВИД КНОПКИ РАЗВЯЗАНЫ.
        //
        // Было: фон баннера зашит числом (#ff4d4f, ДВА одинаковых места — по
        // одному на положение кнопки), текст зашит белым 15px, а кнопка внутри
        // читала `var(--color-bg-surface)`. Настройка «Заливка элемента» через
        // обёртку blockInnerSurface подменяет ровно эту переменную ВСЕМУ
        // поддереву блока — баннер её не читал, а кнопка читала. Отсюда репорт
        // владельца: «крутишь цвет подложки — красится кнопка».
        //
        // Стало: значение поля берёт САМ баннер (запекает цвет в свой фон), а
        // обёртке это поле больше не отдаётся вовсе — см. innerSurfaceFieldsOf
        // в blockInnerSurface.tsx. Мост разорван в источнике: переменной,
        // которую публикует обёртка и читает кнопка, больше нет. Кнопка при
        // этом продолжает читать `--color-bg-surface` ТЕМЫ — то есть у воронки
        // без настроек её вид не меняется ни на пиксель.
        const bannerFill = block.innerBackground
          ? backgroundFillColor(block.innerBackground)
          : undefined;
        // Цвет текста баннера. Публикуем СВОЁ умолчание только когда автор
        // цвет не выбрал: если выбрал — значение уже опубликовала обёртка
        // поверхности, и объявить переменную здесь значило бы перебить выбор
        // автора (объявление ближе к тексту сильнее).
        //
        // Умолчание — исторический БЕЛЫЙ, а не авто-контраст: пара «белый по
        // терракоте» подобрана дизайном, а авто-контраст на #ff4d4f даёт
        // тёмный (яркость 0.27 выше порога 0.179 у pickOnPrimaryColor), и все
        // живые баннеры сменили бы цвет текста. Авто-контраст включается
        // только там, где автор задал СВОЮ заливку: иначе светлая заливка без
        // выбранного цвета текста дала бы белым по белому.
        // Ревизия пачки 4 (WEB-1859): авторская заливка меряется общим порогом
        // непрозрачности — по полупрозрачной плёнке цвет текста не считается,
        // остаётся исторический белый (как у баннера без своей заливки).
        //
        // Возврат приёмки 02.09: замер идёт ТЕМ ЖЕ помощником, что у кнопки и
        // значка (`fillContrastTextColor`), а не своим вызовом порога по одному
        // лишь коду цвета. Плотность приходит ДВУМЯ путями — альфа-канал кода
        // (#rrggbbaa) и плотность ПРЕСЕТА подложки (translucent 0.6, blur 0.35,
        // glass 0.25, плюс ползунок «Плотность»). Здесь мерился только первый,
        // и по «стеклу» поверх светлой страницы баннер считал текст тёмным,
        // хотя кнопка в том же блоке на такой же плёнке текст не считает вовсе.
        // Это ровно класс WEB-1844 «у правила два конца, правят один»: сам
        // порог свели в одно число, а вторую составляющую замера — нет.
        // Заливка баннера собирается `backgroundFillColor` из той же пары
        // (цвет, плотность) — замер обязан мерить ту же поверхность, которую
        // рисует стиль.
        const bannerAutoText = fillContrastTextColor(block.innerBackground);
        const bannerTextDefault = block.innerTextColor?.trim()
          ? undefined
          : (bannerAutoText ?? '#fff');
        const bannerStyle = {
          background: bannerFill ?? '#ff4d4f',
          ...(bannerTextDefault
            ? { '--color-text-on-surface': bannerTextDefault }
            : {}),
          borderRadius: 'var(--border-radius-lg, 12px)',
          padding: '16px 20px',
          marginBottom: 16,
          display: 'flex',
          gap: 12,
          ...(pos === 'default'
            ? {
                alignItems: 'center',
                justifyContent: 'space-between',
              }
            : {
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }),
        } as React.CSSProperties;
        const text = (
          <span
            key="text"
            data-testid={`urgency-banner-text-${block.id}`}
            style={{
              // Читаем ПОДМЕНЯЕМУЮ переменную, а не жёсткий код: так выбор
              // автора («Цвет текста внутри элемента») доезжает до текста, и
              // сторож живых типов видит настройку живой, а не мёртвой.
              color: 'var(--color-text-on-surface, #fff)',
              fontSize: block.fontSize ?? 15,
              fontWeight: 500,
              ...(pos === 'default' ? { flex: 1 } : {}),
            }}
          >
            {richText(block.content)}
          </span>
        );
        const button = (
          <a
            key="btn"
            href={safeHref(block.url)}
            onClick={isEditorPreview ? (e) => e.preventDefault() : undefined}
            style={{
              display: 'inline-block',
              padding: '8px 20px',
              background: 'var(--color-bg-surface, #fff)',
              color: '#ff4d4f',
              borderRadius: 'var(--border-radius-md, 8px)',
              fontSize: 14,
              fontWeight: 700,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              // WEB-1711: своя заливка и свой цвет текста у кнопки — ТЕМ ЖЕ
              // сборщиком, что у всех кнопочных блоков (WEB-1651-2), а не
              // вторым способом: приоритет «выбор автора сильнее
              // авто-контраста по заливке» уже описан и проверен там.
              // `innerBackground` сюда НЕ передаётся сознательно: у этого
              // блока «элемент» — сам баннер, а кнопка на нём отдельная
              // деталь; передать значило бы вернуть тот же мост, только через
              // сборщик вместо переменной. Пустые поля дают {} — вид кнопки в
              // старых воронках не меняется.
              ...buttonSurfaceStyle({
                buttonBackground: block.buttonBackground,
                innerTextColor: block.urgencyButtonTextColor,
              }),
            }}
          >
            {/* WEB-785 (возврат QA 01.08): фолбэк был зашит по-английски. */}
            {richText(
              block.buttonText,
              getPublicQuizStrings(varCtx?.system?.locale).claimLabel,
            )}
          </a>
        );
        return (
          <div data-testid={`urgency-banner-${block.id}`} style={bannerStyle}>
            {pos === 'top' ? [button, text] : [text, button]}
          </div>
        );
      }

      case 'callout': {
        const type = block.calloutType ?? 'info';
        const textColor =
          CALLOUT_VARIANT_COLOR[type] ?? CALLOUT_VARIANT_COLOR.info;
        return (
          <div
            style={{
              background: 'var(--color-bg-surface, #f8fafc)',
              // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
              color:
                'var(--color-text-on-surface, var(--color-text-primary, inherit))',
              border: '1px solid var(--color-border-primary, #e2e8f0)',
              borderRadius: 'var(--border-radius-md, 8px)',
              padding: '12px 16px',
              marginBottom: 16,
            }}
          >
            <p
              style={{
                color: textColor,
                fontSize: block.fontSize ?? 15,
                margin: 0,
              }}
            >
              {richText(block.content)}
            </p>
          </div>
        );
      }

      // Quiz-specific
      case 'progress_text': {
        // Фактическая позиция важнее авторских цифр: после прыжка по логике
        // авторские перестают соответствовать реальному проходу (WEB-832 п.4).
        const current = stepPosition?.current ?? block.progressCurrent ?? 1;
        const total = stepPosition?.total ?? block.progressTotal ?? 5;
        return (
          <p
            style={{
              textAlign: 'center',
              // WEB-1692 юнит 7: текст у блока ОДИН, поэтому кегль общий
              // (`fontSize`), а не своя пара роли. Пусто = прежние 14px.
              fontSize: block.fontSize ?? 14,
              color: 'var(--color-text-secondary, #8c8c8c)',
              marginBottom: 12,
            }}
          >
            {/* WEB-699: stepWord is author-configurable ("Шаг"/"Этап"/...).
             WEB-785: unset now falls back to the funnel-language "Step N of M"
             (was hardcoded English regardless of varCtx.system.locale). */}
            {block.stepWord
              ? `${interp(block.stepWord)} ${current} / ${total}`
              : getPublicQuizStrings(varCtx?.system?.locale).stepOf(
                  current,
                  total,
                )}
          </p>
        );
      }

      case 'conditional_branch':
        return null; // logic-only, no visual output in public render

      case 'multi_step_question':
        return renderMultiStepQuestion(ctx);

      // WEB-447 P1-2b: pre-paywall loader + decorative chart
      case 'loader_processing':
        return renderLoaderProcessing(ctx);

      case 'chart': {
        // WEB-880 (добивка круга аудита 19-22.08): у шести содержательных
        // полей графика две номенклатуры — генерация пишет короткие имена
        // схемы (startLabel/endValue/kind…), кабинет и панель свойств —
        // префиксные (chartStartLabel…). Читать обязаны ОБА: канонизация имён
        // работает на приёме генерации, а в базе лежат воронки, записанные до
        // неё, и миграции данных по решению карточки нет. Пока чтение жило
        // внутри ChartBlockSvg под одним именем, заданные моделью подписи и
        // числа молча подменялись умолчаниями «Today/Goal».
        const chartAi = aiFields(block) as {
          kind?: 'progress_projection' | 'comparison' | 'bars';
          bars?: ChartBar[];
          startLabel?: string;
          endLabel?: string;
          startValue?: string;
          endValue?: string;
          accentColor?: string;
        };
        // WEB-785: locale threaded through so the Today/Goal fallback (when
        // both label names are omitted by generation) matches the funnel's
        // language instead of always English.
        // WEB-1692 юнит 8: накладки ролей читаются ЗДЕСЬ, в ветке своего типа
        // (как и остальные поля графика) — этого требует сторож контракта
        // имён полей; в SVG уезжают уже готовые значения.
        const chartRoles = blockTextRoles(block, 'chart');
        return (
          <ChartBlockSvg
            blockId={block.id}
            kind={block.chartKind ?? chartAi.kind}
            // WEB-3252: столбцы вида «Столбцы»; подписи — тексты автора.
            bars={(block.chartBars ?? chartAi.bars ?? []).map((bar) => ({
              ...bar,
              label: interp(bar.label),
              valueText: interpOptional(bar.valueText),
            }))}
            // WEB-2923: подписи и заголовок — тексты автора, через подстановку.
            // Заголовок график печатает простым текстом (richFieldToPlainText),
            // поэтому подстановка — по простому тексту; пустое остаётся пустым.
            title={
              block.title == null
                ? undefined
                : interp(richFieldToPlainText(asRichField(block.title) ?? ''))
            }
            startLabel={interpOptional(
              block.chartStartLabel ?? chartAi.startLabel,
            )}
            endLabel={interpOptional(block.chartEndLabel ?? chartAi.endLabel)}
            startValue={block.chartStartValue ?? chartAi.startValue}
            endValue={block.chartEndValue ?? chartAi.endValue}
            accentColor={block.chartAccentColor ?? chartAi.accentColor}
            textColor={primaryColor}
            locale={varCtx?.system?.locale}
            titleRole={chartRoles.title}
            secondaryRole={chartRoles.secondary}
          />
        );
      }

      // WEB-447 Волна 0 — 8 block types that previously fell through to
      // `default: return null` below. AI-generated funnels produce these
      // types with short field names (see AiShapedFields above); the editor
      // factory writes prefixed field names. Both are read defensively.

      case 'downsell_popup':
        return renderDownsellPopup(ctx);

      case 'image_choice':
        return renderImageChoice(ctx);

      case 'weight_picker':
        return renderWeightPicker(ctx);

      case 'height_picker':
        return renderHeightPicker(ctx);

      case 'cookie_consent':
        return renderCookieConsent(ctx);

      case 'photo_upload':
        return renderPhotoUpload(ctx);

      case 'return_to_app_button':
        return renderReturnToAppButton(ctx);

      // WEB-1414: действующий элемент восстановления покупок (Apple 3.1.1).
      case 'restore_purchases':
        return renderRestorePurchases(ctx);

      // WEB-1212: «закрыть встроенное окно» (мост {event: 'close'}).
      case 'close_webview_button':
        return renderCloseWebviewButton(ctx);

      default:
        return null;
    }
  })();

  // WEB-2054: та же общая обёртка блока, что у остальных кусков рендерера, —
  // порядок обёрток и пояснения к нему переехали в extendedBlockShell.tsx
  // целиком, чтобы разрез не размножил их по кускам.
  return withExtendedBlockShell(block, rendered, followsExplicitSpacing);
}
